// The script library: one button in the control bar, one dialog behind it. Left is the list —
// presets that ship with the server, then the Storyteller's own saved scripts — with the three
// ways to bring in something new (build, upload, paste). Right is whatever is selected: a
// read-only preview, the tile editor, or the import panel.
//
// Everything funnels to two outcomes: *use* the script in this game (st:importScript) and *save*
// it to the library. Both take standard script JSON, so the editor, an upload and a paste are
// interchangeable from there on.

import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ClipboardPaste, Copy, PenLine, Plus, ScrollText, Trash2, Upload } from "lucide-react";
import { buildScriptJson, rawScriptEntries } from "@cc/shared";
import type { GameView } from "@cc/shared";
import { useGame } from "../../state/game.js";
import { cn } from "../../lib/ui.js";
import {
  deleteFromLibrary, fetchLibrary, roleCount, safeScript, saveToLibrary, type Library, type LibraryScript,
} from "../../lib/scripts.js";
import { Button, Dialog, DialogContent, DialogTrigger, Label, Tooltip } from "../ui/primitives.js";
import { Editor } from "./script-library/Editor.js";
import { ImportPanel } from "./script-library/ImportPanel.js";
import {
  LAYOUT_KEY, LayoutContext, LayoutToggle, readLayout, Roster, TeamSummary, type Layout,
} from "./script-library/tiles.js";

type RawEntries = Map<string, Record<string, unknown>>;

type Pane =
  | { kind: "none" }
  | { kind: "entry"; key: string }
  | { kind: "editor"; nonce: number; editingId: string | null; name: string; ids: string[]; raw: RawEntries }
  | { kind: "import"; nonce: number; seed: string };

export function ScriptLibrary({ view }: { view: GameView }) {
  const send = useGame((s) => s.send);
  const fileRef = useRef<HTMLInputElement>(null);
  const nonce = useRef(0);

  const [open, setOpen] = useState(false);
  const [library, setLibrary] = useState<Library | null>(null);
  const [pane, setPane] = useState<Pane>({ kind: "none" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Replacing the script after roles are out clears seats, so the first click only warns. */
  const [armed, setArmed] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [layout, setLayoutState] = useState<Layout>(readLayout);
  const setLayout = (next: Layout): void => {
    setLayoutState(next);
    try {
      localStorage.setItem(LAYOUT_KEY, next);
    } catch {
      /* a remembered choice is a convenience, not a requirement */
    }
  };

  const hasScript = view.script.characters.length > 0;

  const show = (next: Pane): void => {
    setPane(next);
    setError(null);
    setArmed(false);
    setConfirmDelete(false);
  };

  useEffect(() => {
    if (!open) return;
    setError(null);
    fetchLibrary()
      .then(setLibrary)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Could not load the library."));
  }, [open]);

  const all: (LibraryScript & { key: string; preset: boolean })[] = [
    ...(library?.presets ?? []).map((s) => ({ ...s, key: `preset:${s.id}`, preset: true })),
    ...(library?.saved ?? []).map((s) => ({ ...s, key: `saved:${s.id}`, preset: false })),
  ];
  const selectedEntry = pane.kind === "entry" ? (all.find((e) => e.key === pane.key) ?? null) : null;

  async function attempt(fn: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  /** Puts a script into this game. Returns false when it was held back for confirmation. */
  async function useScript(json: unknown[], confirmed = false): Promise<boolean> {
    if (view.rolesRevealed && !confirmed && !armed) {
      setArmed(true);
      return false;
    }
    const ack = await send("st:importScript", { json: JSON.stringify(json) });
    if (!ack.ok) throw new Error(ack.error);
    setOpen(false);
    return true;
  }

  async function save(name: string, json: unknown[], id?: string): Promise<LibraryScript> {
    const saved = await saveToLibrary({ ...(id ? { id } : {}), name, json });
    setLibrary(await fetchLibrary());
    return saved;
  }

  const saveAndUse = (name: string, json: unknown[], id?: string): void =>
    void attempt(async () => {
      if (view.rolesRevealed && !armed) {
        setArmed(true);
        return;
      }
      await save(name, json, id);
      await useScript(json, true);
    });

  function openEditor(entry: LibraryScript | null, isCopy: boolean): void {
    const script = entry ? safeScript(entry.json) : null;
    show({
      kind: "editor",
      nonce: ++nonce.current,
      editingId: entry && !isCopy ? entry.id : null,
      name: entry ? (isCopy ? `${entry.name} (copy)` : entry.name) : "",
      ids: script?.characters.map((c) => c.id) ?? [],
      raw: entry ? rawScriptEntries(entry.json) : new Map(),
    });
  }

  const openImport = (seed: string): void => show({ kind: "import", nonce: ++nonce.current, seed });

  const armedNote = armed && (
    <p className="text-xs text-evil">
      Roles are already out. Seats whose character isn't on the new script will be cleared — press again to
      replace the script.
    </p>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Tooltip content="Choose a preset or saved script, build one, or upload or paste script JSON. Official characters only.">
        <DialogTrigger asChild>
          <Button variant={hasScript ? "secondary" : "primary"} className="max-w-56">
            <ScrollText className="size-4 shrink-0" />
            <span className="truncate">{hasScript ? view.script.name : "Choose script"}</span>
          </Button>
        </DialogTrigger>
      </Tooltip>

      <DialogContent
        title="Script Library"
        className="w-[min(60rem,calc(100vw-2rem))]"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement | null)?.focus();
        }}
      >
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void file.text().then(openImport);
            e.target.value = "";
          }}
        />

        {/* A fixed height, so searching or switching panes never resizes the dialog — the lists scroll inside it. */}
        <LayoutContext.Provider value={{ layout, setLayout }}>
        <div className="grid h-[min(32rem,calc(85vh-8rem))] gap-4 md:grid-cols-[14rem_1fr]">
          {/* --- left: what you can pick, and how to add more --- */}
          <div className="cc-scroll cc-scroll-strong flex min-h-0 flex-col gap-3 overflow-y-auto pr-1">
            <div className="flex flex-col gap-1.5">
              <Button variant="primary" onClick={() => openEditor(null, false)}>
                <Plus className="size-4" /> Create New script
              </Button>
              <div className="grid grid-cols-2 gap-1.5">
                <Button onClick={() => fileRef.current?.click()}>
                  <Upload className="size-4" /> Upload
                </Button>
                <Button onClick={() => openImport("")}>
                  <ClipboardPaste className="size-4" /> Paste
                </Button>
              </div>
            </div>

            {(
              [
                ["Presets", all.filter((e) => e.preset)],
                ["My scripts", all.filter((e) => !e.preset)],
              ] as const
            ).map(([title, entries]) => (
              <div key={title}>
                <Label>{title}</Label>
                <ul className="mt-1 flex flex-col gap-1">
                  {entries.map((e) => (
                    <li key={e.key}>
                      <button
                        type="button"
                        onClick={() => show({ kind: "entry", key: e.key })}
                        aria-current={pane.kind === "entry" && pane.key === e.key}
                        className={cn(
                          "flex w-full items-baseline gap-2 rounded border px-2 py-1.5 text-left text-sm transition",
                          pane.kind === "entry" && pane.key === e.key
                            ? "border-brass bg-brass/20 text-parchment"
                            : "border-ink-line text-parchment-dim hover:text-parchment",
                        )}
                      >
                        <span className="truncate">{e.name}</span>
                        <span className="ml-auto shrink-0 text-xs tabular-nums">{roleCount(e.json)}</span>
                        {hasScript && e.name === view.script.name && (
                          <Tooltip content="This is the script the game is using now.">
                            <Check className="size-3 shrink-0 text-brass" />
                          </Tooltip>
                        )}
                      </button>
                    </li>
                  ))}
                  {library && entries.length === 0 && (
                    <li className="px-1 text-xs text-parchment-dim">
                      {title === "Presets" ? "None installed." : "Nothing saved yet."}
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>

          {/* --- right: the selected script, the editor, or the import panel --- */}
          <div className="flex min-h-0 min-w-0 flex-col gap-3">
            {error && (
              <p className="rounded border border-evil/60 bg-evil/15 px-2 py-1.5 text-sm text-parchment" role="alert">
                {error}
              </p>
            )}

            {pane.kind === "none" && (
              <p className="py-10 text-center text-sm text-parchment-dim">
                {hasScript ? `Using “${view.script.name}”. ` : "No script yet. "}
                Pick one on the left, or make a new one.
              </p>
            )}

            {selectedEntry && (
              <SelectedScript
                entry={selectedEntry}
                busy={busy}
                armedNote={armedNote}
                confirmDelete={confirmDelete}
                onUse={() => void attempt(async () => void (await useScript(selectedEntry.json)))}
                onEdit={() => openEditor(selectedEntry, selectedEntry.preset)}
                onAskDelete={() => setConfirmDelete(true)}
                onCancelDelete={() => setConfirmDelete(false)}
                onDelete={() =>
                  void attempt(async () => {
                    await deleteFromLibrary(selectedEntry.id);
                    setLibrary(await fetchLibrary());
                    show({ kind: "none" });
                  })
                }
              />
            )}

            {pane.kind === "editor" && (
              <>
                <Editor
                  key={pane.nonce}
                  initial={{ name: pane.name, ids: pane.ids }}
                  busy={busy}
                  onSave={(name, ids) =>
                    saveAndUse(name, buildScriptJson(name, ids, pane.raw), pane.editingId ?? undefined)
                  }
                  onUse={(name, ids) =>
                    void attempt(async () => void (await useScript(buildScriptJson(name, ids, pane.raw))))
                  }
                />
                {armedNote}
              </>
            )}

            {pane.kind === "import" && (
              <>
                <ImportPanel
                  key={pane.nonce}
                  seed={pane.seed}
                  busy={busy}
                  onSave={(name, json) => saveAndUse(name, json)}
                  onUse={(json) => void attempt(async () => void (await useScript(json)))}
                />
                {armedNote}
              </>
            )}
          </div>
        </div>
        </LayoutContext.Provider>
      </DialogContent>
    </Dialog>
  );
}

function SelectedScript({
  entry,
  busy,
  armedNote,
  confirmDelete,
  onUse,
  onEdit,
  onAskDelete,
  onCancelDelete,
  onDelete,
}: {
  entry: LibraryScript & { preset: boolean };
  busy: boolean;
  armedNote: React.ReactNode;
  confirmDelete: boolean;
  onUse: () => void;
  onEdit: () => void;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onDelete: () => void;
}) {
  const script = useMemo(() => safeScript(entry.json), [entry]);
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex items-baseline gap-2">
        <h3 className="text-base font-semibold text-parchment">{entry.name}</h3>
        <span className="text-xs text-parchment-dim">{entry.preset ? "Preset" : "Saved"}</span>
        <LayoutToggle className="ml-auto self-center" />
      </div>
      {script ? (
        <Roster script={script} />
      ) : (
        <p className="text-sm text-evil">This script can't be read any more.</p>
      )}
      {script && (
        <TeamSummary teams={script.characters.map((c) => c.team)} />
      )}
      {armedNote}
      <div className="flex flex-wrap items-center gap-1.5 border-t border-ink-line pt-3">
        {entry.preset ? (
          <Button onClick={onEdit}>
            <Copy className="size-4" /> Duplicate to edit
          </Button>
        ) : confirmDelete ? (
          <>
            <Button variant="danger" disabled={busy} onClick={onDelete}>
              Really delete
            </Button>
            <Button variant="ghost" onClick={onCancelDelete}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onEdit}>
              <PenLine className="size-4" /> Edit
            </Button>
            <Button variant="ghost" onClick={onAskDelete}>
              <Trash2 className="size-4" /> Delete
            </Button>
          </>
        )}
        <Button variant="primary" className="ml-auto" disabled={busy || !script} onClick={onUse}>
          Use this script
        </Button>
      </div>
    </div>
  );
}
